import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

/** Treat angular-editor placeholder HTML as empty (e.g. `<p><br></p>`). */
function richTextRequired(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value == null || value === '') {
    return { required: true };
  }
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length === 0 ? { required: true } : null;
}

@Component({
  selector: 'app-info-page',
  templateUrl: './info-page.component.html',
  styleUrls: ['./info-page.component.scss']
})
export class InfoPageComponent implements OnInit {
  isSubmitted: any;
  infoPageForm: any;
  isLoading: boolean = false;
  isSaving: boolean = false;
  /** Language last successfully loaded — used to revert cancelled language switches. */
  private currentLanguage = 'en';
  /** Incremented on each load so stale responses cannot overwrite newer language content. */
  private loadSeq = 0;

  selectedLanguage: any = [
    { lable: 'English', value: 'en' },
    { lable: 'Danish', value: 'da' }
  ];

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '200px',
    translate: 'no'
  };

  constructor(
    public service: ApiService,
    public toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.infoPageForm = new FormGroup({
      language: new FormControl('en'),
      content: new FormControl('', [Validators.required, richTextRequired])
    });
    this.currentLanguage = this.infoPageForm.value.language;
    this.getInfoPage();
  }

  onLanguageChange(): void {
    const newLang = this.infoPageForm.value.language;
    if (newLang === this.currentLanguage) {
      return;
    }

    if (this.infoPageForm.dirty) {
      Swal.fire({
        title: 'Unsaved changes',
        text: 'You have unsaved changes. Switch language and discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard changes',
        cancelButtonText: 'Stay'
      }).then((result) => {
        if (result.isConfirmed) {
          this.currentLanguage = newLang;
          this.getInfoPage();
        } else {
          this.infoPageForm.patchValue({ language: this.currentLanguage }, { emitEvent: false });
        }
      });
      return;
    }

    this.currentLanguage = newLang;
    this.getInfoPage();
  }

  getInfoPage(): void {
    const seq = ++this.loadSeq;
    this.isLoading = true;
    this.editorConfig = { ...this.editorConfig, editable: false };
    const lang = this.infoPageForm.value.language;

    this.service.getRequest(`${ApiUrl.getInfoPage}${lang}`).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.isLoading = false;
        this.editorConfig = { ...this.editorConfig, editable: true };
        const loadedLang = res.response.data.language ? res.response.data.language : lang;
        this.infoPageForm.patchValue({
          language: loadedLang,
          content: res.response.data.content
        });
        this.currentLanguage = loadedLang;
        this.infoPageForm.markAsPristine();
      },
      error: (err: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.isLoading = false;
        this.editorConfig = { ...this.editorConfig, editable: true };

        if (err.status === 404) {
          this.infoPageForm.patchValue({ content: '' });
          this.infoPageForm.get('content')?.markAsUntouched();
          this.currentLanguage = lang;
          this.infoPageForm.markAsPristine();
          return;
        }

        this.toastr.error(err.error?.response?.message || 'Failed to load info page content');
        this.infoPageForm.patchValue({ language: this.currentLanguage }, { emitEvent: false });
      }
    });
  }

  onSave(): void {
    this.isSubmitted = true;
    if (this.infoPageForm.invalid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    const body = {
      language: this.infoPageForm.value.language,
      content: this.infoPageForm.value.content
    };
    this.service.postRequest(ApiUrl.createInfoPage, body).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.currentLanguage = this.infoPageForm.value.language;
        this.infoPageForm.markAsPristine();
        this.toastr.success(res.response.message);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toastr.error(err.error?.response?.message || 'Failed to save info page');
      }
    });
  }
}
